import React, { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
// 운영 단일 클라이언트(services/api)는 baseURL 에 '/api' 를 포함한다.
// (기존 '../api/client' 는 baseURL 에 '/api' 가 없어 프로덕션에서 사이드문의가 404 무음 유실되던 버그가 있었음)
import api from '../services/api';
import { DEFAULT_CONTACT_PHONE } from '../domain/inquiry/resolveContactPhone';
import { buildLeadPayload, generateEventId, trackLead } from '../utils/tracking';
import {
  PrivacyConsent,
  CallKakaoButtons,
  InquiryGuideCard,
  InquiryDone,
  fieldLabelClass,
  inputClass,
} from './inquiry/InquiryParts';

/**
 * 일반 상담 문의 (/side-inquiry) — v2 목업 06 레이아웃 (2026-09-22 v2h).
 * 제출: POST /properties/side-inquiry/ {name, phone, property_name} — 기존 그대로.
 * 현장이 정해지지 않은 문의라 전화는 대표번호 1533-8124.
 */
const GeneralInquiryPage: React.FC = () => {
  const [formData, setFormData] = useState({ name: '', phone: '', propertyName: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.phone.trim()) {
      alert('이름과 연락처를 입력해주세요.');
      return;
    }
    if (!agreed) {
      alert('개인정보 수집·이용에 동의해주세요.');
      return;
    }
    setSubmitting(true);
    try {
      const eventId = generateEventId();
      await api.post('/properties/side-inquiry/', {
        name: formData.name,
        phone: formData.phone,
        property_name: formData.propertyName,
        ...buildLeadPayload(eventId),  // 광고 추적(개인정보 없음) — 서버 CAPI 와 같은 event_id
      });
      // 저장 성공(2xx) 뒤에만 Lead. 현장 미지정 일반 문의 — 입력한 현장명 자유 텍스트는 보내지 않음(서버와 동일)
      trackLead({ name: '일반 문의' }, eventId);
      setSubmitted(true);
    } catch {
      alert('전송 실패. 잠시 후 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  useSeoMeta({ title: '사이드문의 | 부냥' });

  if (submitted) {
    return (
      <div className="min-h-screen bg-paper px-4 pb-24 pt-2 font-sans">
        <div className="mx-auto max-w-[480px]">
          <InquiryDone
            title="접수되었습니다"
            customerPhone={formData.phone}
            propertyTitle={formData.propertyName || null}
            phone={DEFAULT_CONTACT_PHONE}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper pb-24 font-sans">
      <div className="mx-auto flex max-w-[1200px] flex-col gap-4 px-4 pt-4 lg:flex-row lg:items-start lg:gap-6 lg:px-10 lg:pt-8">
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="flex flex-col gap-1">
            <h1 className="text-[22px] font-bold tracking-tight text-ink lg:text-[26px]">상담 문의</h1>
            <span className="text-[13.5px] text-muted">남겨주시면 담당 상담원이 영업시간 내 회신드립니다.</span>
          </div>

          <form method="post" onSubmit={handleSubmit} className="flex flex-col gap-4 rounded-ca-3xl bg-frame p-4 shadow-ca-card lg:p-6">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div>
                <label htmlFor="g-name" className={fieldLabelClass}>
                  이름 <span className="text-accent">*</span>
                </label>
                <input
                  id="g-name"
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="홍길동"
                  required
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="g-phone" className={fieldLabelClass}>
                  연락처 <span className="text-accent">*</span>
                </label>
                <input
                  id="g-phone"
                  type="tel"
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="010-1234-5678"
                  required
                  className={inputClass}
                />
              </div>
              <div className="lg:col-span-2">
                <label htmlFor="g-property" className={fieldLabelClass}>
                  관심 단지
                </label>
                <input
                  id="g-property"
                  type="text"
                  value={formData.propertyName}
                  onChange={e => setFormData({ ...formData, propertyName: e.target.value })}
                  placeholder="예: 호반써밋, 힐스테이트 등"
                  className={inputClass}
                />
              </div>
            </div>

            <PrivacyConsent checked={agreed} onChange={setAgreed} disabled={submitting} />

            <button
              type="submit"
              disabled={submitting}
              className="h-[52px] w-full rounded-ca-xl bg-accent text-[16px] font-bold text-white shadow-ca-cta transition-colors hover:bg-accent-dark disabled:cursor-not-allowed disabled:opacity-45"
            >
              {submitting ? '전송 중...' : '상담 신청하기'}
            </button>

            <div className="lg:hidden">
              <CallKakaoButtons phone={DEFAULT_CONTACT_PHONE} />
            </div>
          </form>

          <Link to="/" className="self-center text-[13px] font-semibold text-muted lg:self-start">
            메인으로 돌아가기
          </Link>
        </div>

        <aside className="hidden w-[340px] shrink-0 flex-col gap-4 lg:flex">
          <div className="flex flex-col gap-3 rounded-ca-3xl bg-frame p-4 shadow-ca-card">
            <div className="flex flex-col gap-0.5">
              <span className="text-[12px] font-semibold text-muted">상담 전화</span>
              <b className="num text-[22px] font-bold text-ink">{DEFAULT_CONTACT_PHONE.display}</b>
            </div>
            <CallKakaoButtons phone={DEFAULT_CONTACT_PHONE} stacked />
          </div>
          <InquiryGuideCard />
        </aside>
      </div>
    </div>
  );
};

export default GeneralInquiryPage;
